import { useQuery } from '@tanstack/react-query';

import { sessionsService } from '@/features/sessions/services';
import type { BundleTranscriptSegment } from '@/features/sessions/types';

const NO_SEGMENTS: BundleTranscriptSegment[] = [];

/**
 * Lines the transcription agent already saved for a live call, so the live
 * panel is not empty after a rejoin or reload. Pass null for viewers who may
 * not read the saved transcript (clients): the endpoint is staff-only.
 *
 * It keeps its own cache entry, dropped when the call screen unmounts. Sharing
 * the post-session drawer's bundle would leave that drawer showing the
 * transcript as it was at call start after a short call. No retries: a
 * refusal just means the panel starts from the live feed, as before.
 */
export function useSavedCallTranscript(sessionId: string | null): BundleTranscriptSegment[] {
  const query = useQuery({
    queryKey: ['video', 'saved-call-transcript', sessionId ?? ''],
    queryFn: async () => {
      if (!sessionId) throw new Error('missing sessionId');
      const bundle = await sessionsService.getClinicalBundle(sessionId);
      return bundle.transcript.segments;
    },
    enabled: sessionId !== null && sessionId !== '',
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return query.data ?? NO_SEGMENTS;
}
